/**
 * ProfileView.js
 *
 * Copyright (c) 2026 Eric Seigne <eric.seigne@cap-rel.fr>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import { i18n } from '../i18n/index.js'
import { Gas } from '../core/Gas.js'
import { toggleFullscreen } from '../utils/viewUtils.js'

export class ProfileView {
  constructor(container) {
    this.container = container
    this.selectedSegmentIndex = null
    this.interactivePoints = []
    // Profile mode: 'ccr' or 'bailout'
    this.profileMode = 'ccr'
    // Zoom and pan state
    this.scale = 1
    this.offsetX = 0
    this.offsetY = 0
    this.minScale = 1
    this.maxScale = 5
    // Touch state
    this.lastPinchDistance = null
    this.lastTouchX = null
    this.lastTouchY = null
    this.isPanning = false
    this.lastTapTime = 0
    // Store subscription
    this.unsubscribe = null
    // Hidden chart layers (toggled via legend clicks)
    this.hiddenLayers = new Set()
    // Colors for school mode comparison curves
    this.schoolModeColors = [
      'rgba(251, 146, 60, 0.7)',   // orange
      'rgba(34, 211, 238, 0.7)',   // cyan
      'rgba(250, 204, 21, 0.7)'    // yellow
    ]
    // Tissue detail panel state
    this.expandedCompartment = null
    this.currentSegmentDepth = null
    this.currentSegmentCompartments = null
    this.currentSegmentTissueInfo = null
    this.currentSegmentCeiling = null
  }

  t(key, params) {
    return i18n.t(key, params)
  }

  // Build the profile page: canvas, tissue bars, segment info, zoom controls, tabs
  render() {
    const { ccr } = window.store.state
    const results = window.store.state.results
    const hasBothPlans = ccr.enabled && results && results.ccr && results.bailout

    this.container.innerHTML = `
      <style>
        #profile-fullscreen-container:fullscreen {
          display: flex !important;
          flex-direction: row;
          gap: 1rem;
          padding: 1rem;
          overflow: hidden;
        }
        #profile-fullscreen-container:fullscreen > * + * { margin-top: 0 !important; }
        #profile-fullscreen-container:fullscreen > #profile-chart-panel {
          flex: 1; min-width: 0; display: flex; flex-direction: column;
        }
        #profile-fullscreen-container:fullscreen > #profile-chart-panel > .card {
          flex: 1; display: flex; flex-direction: column;
        }
        #profile-fullscreen-container:fullscreen > #profile-chart-panel .card-body {
          flex: 1; display: flex; flex-direction: column;
        }
        #profile-fullscreen-container:fullscreen > #profile-chart-panel .relative {
          flex: 1; min-height: 0; display: flex; flex-direction: column;
        }
        #profile-fullscreen-container:fullscreen #profile-canvas {
          flex: 1; min-height: 0; height: auto !important;
        }
        #profile-fullscreen-container:fullscreen > #profile-side-panel {
          width: 350px; flex-shrink: 0; overflow-y: auto; max-height: 100%;
        }
      </style>
      <div id="profile-fullscreen-container" class="space-y-4 pb-20">
        <div id="profile-chart-panel">
        <div class="card bg-base-100 shadow-sm">
          <div class="card-body p-4">
            <div class="flex justify-between items-center">
              <h2 class="card-title text-sm">${this.t('profile_title')}</h2>
              <div class="flex items-center gap-1">
                <button id="chart-scale-down" class="btn btn-ghost btn-xs" title="Scale -">-</button>
                <span id="chart-scale-label" class="text-xs opacity-70">${window.store?.state?.settings?.chartScale || 1}x</span>
                <button id="chart-scale-up" class="btn btn-ghost btn-xs" title="Scale +">+</button>
                <button id="fullscreen-profile-btn" class="btn btn-ghost btn-xs" title="${this.t('profile_fullscreen') || 'Fullscreen'}">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                </button>
              </div>
              ${hasBothPlans ? `
                <div class="tabs tabs-boxed tabs-xs">
                  <button class="tab profile-tab ${this.profileMode === 'ccr' ? 'tab-active' : ''}" data-mode="ccr">CCR</button>
                  <button class="tab profile-tab ${this.profileMode === 'bailout' ? 'tab-active' : ''}" data-mode="bailout">Bailout</button>
                </div>
              ` : ''}
            </div>
            <div class="relative">
              <canvas id="profile-canvas" class="w-full bg-base-200 rounded-lg touch-none" style="height: 300px" height="300"></canvas>
              <button id="reset-zoom-btn" class="btn btn-xs btn-ghost absolute top-2 right-2 opacity-0 transition-opacity" title="Reset zoom">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                </svg>
              </button>
            </div>
            <div id="school-mode-legend" class="flex flex-wrap justify-center gap-3 mt-2 text-xs"></div>
          </div>
        </div>
        </div>

        <div id="profile-side-panel" class="space-y-4">
        <div id="school-mode-table-card" class="card bg-base-100 shadow-sm hidden">
          <div class="card-body p-4">
            <h2 class="card-title text-sm">${this.t('profile_school_compare')}</h2>
            <div id="school-mode-table" class="overflow-x-auto"></div>
          </div>
        </div>

        <div class="card bg-base-100 shadow-sm">
          <div class="card-body p-4">
            <h2 class="card-title text-sm">
              ${this.t('profile_saturation')}
              <span id="segment-info" class="text-xs font-normal opacity-70 ml-2"></span>
            </h2>
            <div id="tissue-bars" class="space-y-1">
              ${this.renderTissueBars(null)}
            </div>
          </div>
        </div>
        </div>
      </div>
    `

    this.drawProfile()
    this.bindEvents()

    // Redraw on fullscreen change (enter and exit)
    this.fullscreenHandler = () => setTimeout(() => this.drawProfile(), 100)
    document.addEventListener('fullscreenchange', this.fullscreenHandler)

    // Subscribe to store changes to auto-update profile
    this.unsubscribe = window.store.subscribe(() => {
      this.drawProfile()
    })
  }

  destroy() {
    if (this.unsubscribe) {
      this.unsubscribe()
      this.unsubscribe = null
    }
    if (this.fullscreenHandler) {
      document.removeEventListener('fullscreenchange', this.fullscreenHandler)
      this.fullscreenHandler = null
    }
  }

  getTissueSpeed(halfTime) {
    if (halfTime <= 18.5) return this.t('tissue_speed_fast')
    if (halfTime <= 109) return this.t('tissue_speed_medium')
    return this.t('tissue_speed_slow')
  }

  renderTissueBars(compartments, tissueInfo, ceiling, depth) {
    let html = ''

    // Leading compartment banner
    if (compartments && tissueInfo) {
      const leadingDesc = this.t(`tissue_desc_${tissueInfo.compartment}`)
      const ceilVal = Math.round(ceiling || 0)
      const gfPercent = tissueInfo.gfPercent || 0
      const gfLimit = tissueInfo.gfUsed || 0
      const settings = window.store?.state?.settings || {}
      const gfLow = Math.round((settings.gfLow || 0.3) * 100)
      const gfHigh = Math.round((settings.gfHigh || 0.85) * 100)
      const params = { tissue: leadingDesc, num: tissueInfo.compartment, ceiling: ceilVal, gf: gfPercent, gfLimit: gfLimit, gfLow, gfHigh }
      const bannerText = ceilVal > 0
        ? this.t('tissue_leading_detail', params)
        : this.t('tissue_leading_no_ceiling', params)
      html += `<div class="text-xs py-2 px-3 mb-3 rounded-sm bg-primary/10 border-l-4 border-primary"><span>${bannerText}</span></div>`
    }

    for (let i = 0; i < 16; i++) {
      const saturation = compartments ? compartments[i].saturation : 0
      const isLeading = compartments && tissueInfo && (tissueInfo.compartment - 1 === i)
      const barClass = saturation > 80 ? 'progress-error' : (saturation > 60 ? 'progress-warning' : 'progress-primary')
      const halfTime = compartments ? Math.round(compartments[i].halfTimeN2) : '-'
      const tissueDesc = this.t(`tissue_desc_${i + 1}`)
      const halfTimeLabel = this.t('tissue_halftime')
      const speedLabel = compartments ? this.getTissueSpeed(compartments[i].halfTimeN2) : ''
      const leadingStyle = isLeading ? 'border-l-4 border-primary pl-2 bg-primary/10 rounded-r' : ''
      const isExpanded = this.expandedCompartment === i
      const expandIcon = isExpanded ? '▾' : '▸'

      html += `
        <div class="mb-1 py-0.5 ${leadingStyle}">
          <div class="flex items-center gap-2 cursor-pointer tissue-bar-toggle" data-compartment="${i}">
            <span class="text-xs w-8 ${isLeading ? 'font-bold text-primary' : ''}"><span class="text-[9px] opacity-40">${expandIcon}</span> C${i + 1}</span>
            <progress class="progress ${barClass} flex-1 h-2 pointer-events-none" value="${saturation}" max="100"></progress>
            <span class="text-xs w-10 text-right ${isLeading ? 'font-bold' : ''}">${saturation}%</span>
          </div>
          <div class="text-[10px] opacity-50 ml-8">${tissueDesc} — ${speedLabel} — ${halfTimeLabel} ${halfTime} min</div>
          ${isExpanded && compartments ? this.renderCompartmentDetail(compartments[i], depth, isLeading, tissueInfo) : ''}
        </div>`
    }
    return html
  }

  renderCompartmentDetail(comp, depth, isLeading, tissueInfo) {
    // Same conversion as Buhlmann.getCompartmentState, which computed the
    // M-value displayed below: a hard coded depth/10+1 made the printed
    // arithmetic contradict its own result as soon as the dive was planned at
    // altitude or in fresh water.
    const pAmb = Gas.pressureAtDepth(depth || 0, window.store?.state?.environment || {})
    const pTotal = comp.pTotal
    const mValue = comp.mValue
    const a = comp.a
    const b = comp.b
    const satPct = mValue > 0 ? (pTotal / mValue * 100) : 0
    const margin = mValue - pAmb
    const isSuperSaturated = pTotal > pAmb
    const gfActual = margin > 0 && isSuperSaturated ? ((pTotal - pAmb) / margin * 100) : 0
    const settings = window.store?.state?.settings || {}
    const gfLow = Math.round((settings.gfLow || 0.3) * 100)
    const gfHigh = Math.round((settings.gfHigh || 0.85) * 100)
    const gfUsed = tissueInfo?.gfUsed || 0

    const f = (v) => v.toFixed(2)

    let gfHtml
    if (!isSuperSaturated) {
      gfHtml = `
        <div class="mb-1">
          <span class="opacity-60">${this.t('tissue_detail_gf_actual')}</span> :
          <span class="font-bold">${this.t('tissue_detail_no_supersaturation')}</span>
          <span class="opacity-50">(pTotal ${f(pTotal)} &le; pAmb ${f(pAmb)})</span>
        </div>
        <div class="mt-2 font-sans text-[10px] opacity-60 leading-snug">
          ${this.t('tissue_detail_undersaturated')}
        </div>`
    } else {
      // Determine GF context: first stop (GF Low), surface (GF High), or interpolated
      let explanationKey
      const params = { saturation: Math.round(satPct), gfActual: Math.round(gfActual), gfHigh, gfLow, gfUsed }
      if (gfUsed <= gfLow + 2) {
        explanationKey = 'tissue_detail_explanation_gf_low'
      } else if (gfUsed >= gfHigh - 2 || (depth || 0) === 0) {
        explanationKey = 'tissue_detail_explanation'
      } else {
        explanationKey = 'tissue_detail_explanation_interpolated'
      }

      gfHtml = `
        <div class="mb-1">
          <span class="opacity-60">${this.t('tissue_detail_gf_actual')}</span> = (pTotal - pAmb) / (M-value - pAmb) = (${f(pTotal)} - ${f(pAmb)}) / (${f(mValue)} - ${f(pAmb)}) = <span class="font-bold">${Math.round(gfActual)}%</span>
        </div>
        <div class="mt-2 font-sans text-[10px] opacity-60 leading-snug">
          ${this.t(explanationKey, params)}
        </div>`
    }

    return `
      <div class="mt-1 mb-2 ml-8 p-2 rounded bg-base-200 text-[11px] font-mono leading-relaxed">
        <div class="font-sans font-semibold text-xs mb-1">${this.t('tissue_detail_title')}</div>
        <div class="opacity-70 mb-1">
          ${this.t('tissue_halftime')}: ${Math.round(comp.halfTimeN2)} min N2${comp.pHe > 0 ? ` / ${Math.round(comp.halfTimeHe)} min He` : ''}
        </div>
        <div class="opacity-70 mb-1">
          ${this.t('tissue_detail_coefficients', { algo: (settings.algorithm || 'zhl16c').toUpperCase().replace('ZHL16', 'ZHL-16'), a: f(a), b: f(b) })}
        </div>
        <div class="mb-1">
          <span class="opacity-60">M-value @ ${Math.round(depth || 0)}m</span> = a + pAmb/b = ${f(a)} + ${f(pAmb)}/${f(b)} = <span class="font-bold">${f(mValue)} bar</span>
        </div>
        <div class="mb-1">
          <span class="opacity-60">${this.t('tissue_detail_saturation')}</span> = pTotal / M-value = ${f(pTotal)} / ${f(mValue)} = <span class="font-bold ${satPct > 80 ? 'text-error' : satPct > 60 ? 'text-warning' : ''}">${Math.round(satPct)}%</span>
        </div>
        ${gfHtml}
      </div>`
  }

  // Convert plan segments into a flat array of {time, depth, ceiling, segment}
  // points suitable for canvas drawing. Handles descent, ascent, bottom, and
  // stop segments, and appends a synthetic surface point at the end.
  buildProfilePoints(plan) {
    if (!plan || !plan.segments || plan.segments.length === 0) return null

    const points = []
    let currentTime = 0
    let currentDepth = 0
    const ascentRate = window.store.state.settings.ascentRate || 9 // m/min

    // Start at surface
    points.push({ time: 0, depth: 0, ceiling: 0, segment: null })

    for (const seg of plan.segments) {
      if (seg.type === 'descent' || seg.type === 'ascent') {
        currentTime += seg.time
        currentDepth = seg.endDepth
        points.push({
          time: currentTime,
          depth: seg.endDepth,
          ceiling: Math.max(0, seg.ceiling || 0),
          segment: seg
        })
      } else if (seg.type === 'gasChange' || seg.type === 'flush') {
        // Gas switch or loop flush: the diver stays at depth for its minutes.
        // A flush used to have no point at all, and the ascent that followed
        // it absorbed its minutes into a slope four times too gentle.
        if (seg.time > 0) {
          currentTime += seg.time
          points.push({
            time: currentTime,
            depth: seg.depth,
            ceiling: Math.max(0, seg.ceiling || 0),
            segment: seg
          })
        }
      } else if (seg.type === 'bottom' || seg.type === 'stop') {
        if (currentDepth > seg.depth) {
          const ascentTime = (currentDepth - seg.depth) / ascentRate
          currentTime += ascentTime
          points.push({
            time: currentTime,
            depth: seg.depth,
            ceiling: Math.max(0, seg.ceiling || 0),
            segment: null
          })
        } else if (currentDepth !== seg.depth) {
          points.push({
            time: currentTime,
            depth: seg.depth,
            ceiling: Math.max(0, seg.ceiling || 0),
            segment: null
          })
        }
        currentDepth = seg.depth
        currentTime += seg.time
        points.push({
          time: currentTime,
          depth: seg.depth,
          ceiling: Math.max(0, seg.ceiling || 0),
          segment: seg
        })
      }
    }

    // End at surface
    const lastPoint = points[points.length - 1]
    if (lastPoint.depth > 0) {
      let lastCompartments = null
      for (let i = plan.segments.length - 1; i >= 0; i--) {
        if (plan.segments[i].compartments) {
          lastCompartments = plan.segments[i].compartments
          break
        }
      }

      const surfaceSegment = {
        type: 'surface',
        depth: 0,
        endDepth: 0,
        time: 0,
        runtime: lastPoint.segment?.runtime || currentTime,
        compartments: lastCompartments,
        ceiling: 0
      }

      points.push({
        time: lastPoint.time + 1,
        depth: 0,
        ceiling: 0,
        segment: surfaceSegment
      })
    }

    return points
  }

  getProfileData() {
    const results = window.store.state.results
    if (!results) return null

    let plan
    if (this.profileMode === 'ccr' && results.ccr) {
      plan = results.ccr
    } else if (this.profileMode === 'bailout' && results.bailout) {
      plan = results.bailout
    } else {
      plan = results.ccr || results.bailout
    }

    return this.buildProfilePoints(plan)
  }

  // Convert depth to Y coordinate, handling non-linear scale for deco zone expansion
  toY(depth) {
    const availableHeight = this.canvasHeight - this.padding * 2
    if (!this.useNonLinearScale) {
      return this.padding + (depth / this.maxDepth) * availableHeight
    }
    if (depth <= this.firstStopDepth) {
      return this.padding + (depth / this.firstStopDepth) * this.decoZoneRatio * availableHeight
    }
    const depthInDeepZone = depth - this.firstStopDepth
    const deepZoneRange = this.maxDepth - this.firstStopDepth
    return this.padding + this.decoZoneRatio * availableHeight + (depthInDeepZone / deepZoneRange) * (1 - this.decoZoneRatio) * availableHeight
  }

  // Convert time to X coordinate
  toX(time) {
    return this.padding + (time / this.maxTime) * (this.canvasWidth - this.padding * 2)
  }

  // Draw depth and time grid lines
  drawGrid(ctx) {
    const cs = this.chartScale
    ctx.strokeStyle = 'rgba(255,255,255,0.1)'
    ctx.lineWidth = 1 * cs / this.scale

    for (let d = 0; d <= this.maxDepth; d += this.depthStep) {
      const y = this.toY(d)
      ctx.beginPath()
      ctx.moveTo(this.padding, y)
      ctx.lineTo(this.canvasWidth - this.padding, y)
      ctx.stroke()
    }

    // Dashed transition line between deco zone and deep zone
    if (this.useNonLinearScale) {
      ctx.strokeStyle = 'rgba(255,255,255,0.3)'
      ctx.setLineDash([2 * cs / this.scale, 2 * cs / this.scale])
      const transitionY = this.toY(this.firstStopDepth)
      ctx.beginPath()
      ctx.moveTo(this.padding, transitionY)
      ctx.lineTo(this.canvasWidth - this.padding, transitionY)
      ctx.stroke()
      ctx.setLineDash([])
      ctx.strokeStyle = 'rgba(255,255,255,0.1)'
    }

    for (let t = 0; t <= this.maxTime; t += this.timeStep) {
      const x = this.toX(t)
      ctx.beginPath()
      ctx.moveTo(x, this.padding)
      ctx.lineTo(x, this.canvasHeight - this.padding)
      ctx.stroke()
    }
  }

  // Draw main GF ceiling curve (dashed) and depth profile line (solid).
  // In normal mode: green profile with filled area beneath; in school mode: red.
  drawMainProfile(ctx, profileData) {
    if (this.hiddenLayers.has('ceiling')) return
    const cs = this.chartScale
    const isSchoolMode = window.store.state.settings.schoolMode &&
      window.store.state.results?.schoolMode?.length > 0
    const profileColor = isSchoolMode ? 'rgba(248, 114, 114, 0.8)' : 'rgba(74, 222, 128, 0.8)'
    const fillColor = isSchoolMode ? null : 'rgba(74, 222, 128, 0.12)'

    // Ceiling curve (dashed)
    ctx.strokeStyle = profileColor
    ctx.lineWidth = 1.5 * cs / this.scale
    ctx.setLineDash([4 * cs / this.scale, 4 * cs / this.scale])
    ctx.beginPath()
    let first = true
    for (const point of profileData) {
      const x = this.toX(point.time)
      const y = this.toY(point.ceiling)
      if (first) { ctx.moveTo(x, y); first = false }
      else ctx.lineTo(x, y)
    }
    ctx.stroke()
    ctx.setLineDash([])

    // Filled area beneath the depth profile (normal mode only)
    if (fillColor) {
      ctx.beginPath()
      const surfaceY = this.toY(0)
      profileData.forEach((point, i) => {
        const x = this.toX(point.time)
        const y = this.toY(point.depth)
        if (i === 0) ctx.moveTo(x, surfaceY)
        ctx.lineTo(x, y)
      })
      // Close back to surface
      const lastX = this.toX(profileData[profileData.length - 1].time)
      ctx.lineTo(lastX, surfaceY)
      ctx.closePath()
      ctx.fillStyle = fillColor
      ctx.fill()
    }

    // Profile line (solid)
    ctx.strokeStyle = profileColor
    ctx.lineWidth = 1.5 * cs / this.scale
    ctx.beginPath()
    profileData.forEach((point, i) => {
      const x = this.toX(point.time)
      const y = this.toY(point.depth)
      if (i === 0) ctx.moveTo(x, y)
      else ctx.lineTo(x, y)
    })
    ctx.stroke()

  }

  // Draw school mode comparison curves (ceiling + profile for each preset)
  drawSchoolModeProfiles(ctx, schoolModeData) {
    this.schoolModeProfiles = []
    if (!schoolModeData || schoolModeData.length === 0) return
    const cs = this.chartScale

    schoolModeData.forEach((comp, idx) => {
      if (this.hiddenLayers.has(`school-${idx}`)) return
      const color = this.schoolModeColors[idx % this.schoolModeColors.length]
      const compPoints = this.buildProfilePoints(comp)
      if (!compPoints) return
      this.schoolModeProfiles.push({ preset: comp.preset, color, points: compPoints })

      // Ceiling curve (dashed)
      ctx.strokeStyle = color
      ctx.lineWidth = 1.5 * cs / this.scale
      ctx.setLineDash([6 * cs / this.scale, 3 * cs / this.scale])
      ctx.beginPath()
      let firstCeil = true
      for (const point of compPoints) {
        const x = this.toX(point.time)
        const y = this.toY(point.ceiling)
        if (firstCeil) { ctx.moveTo(x, y); firstCeil = false }
        else ctx.lineTo(x, y)
      }
      ctx.stroke()
      ctx.setLineDash([])

      // Profile line (solid)
      ctx.strokeStyle = color
      ctx.lineWidth = 1.5 * cs / this.scale
      ctx.beginPath()
      let firstProf = true
      for (const point of compPoints) {
        const x = this.toX(point.time)
        const y = this.toY(point.depth)
        if (firstProf) { ctx.moveTo(x, y); firstProf = false }
        else ctx.lineTo(x, y)
      }
      ctx.stroke()

    })
  }

  // Draw clickable points at each segment boundary for all visible curves
  drawInteractivePoints(ctx, profileData, schoolModeData) {
    this.interactivePoints = []
    const cs = this.chartScale
    const pointRadius = 8 * cs / this.scale

    const drawPoints = (points, fillColor, layerPrefix) => {
      points.forEach((point, i) => {
        if (!point.segment) return
        const x = this.toX(point.time)
        const y = this.toY(point.depth)
        const pointId = `${layerPrefix}-${i}`

        this.interactivePoints.push({
          x: x * this.scale + this.offsetX,
          y: y * this.scale + this.offsetY,
          radius: 8,
          segment: point.segment,
          index: pointId
        })

        ctx.beginPath()
        ctx.arc(x, y, pointRadius, 0, Math.PI * 2)
        if (this.selectedSegmentIndex === pointId) {
          ctx.fillStyle = '#f472b6'
          ctx.fill()
          ctx.strokeStyle = '#fff'
          ctx.lineWidth = 2 * cs / this.scale
          ctx.stroke()
        } else {
          ctx.fillStyle = fillColor
          ctx.fill()
          ctx.strokeStyle = 'rgba(255,255,255,0.5)'
          ctx.lineWidth = 1 * cs / this.scale
          ctx.stroke()
        }
      })
    }

    if (!this.hiddenLayers.has('ceiling')) {
      const isSchoolMode = window.store.state.settings.schoolMode &&
        window.store.state.results?.schoolMode?.length > 0
      const mainColor = isSchoolMode ? 'rgba(248, 114, 114, 0.8)' : 'rgba(74, 222, 128, 0.8)'
      drawPoints(profileData, mainColor, 'main')
    }

    if (schoolModeData && schoolModeData.length > 0) {
      schoolModeData.forEach((comp, idx) => {
        if (this.hiddenLayers.has(`school-${idx}`)) return
        const color = this.schoolModeColors[idx % this.schoolModeColors.length]
        const compPoints = this.buildProfilePoints(comp)
        if (!compPoints) return
        drawPoints(compPoints, color, `school-${idx}`)
      })
    }
  }

  // Draw GF Low/High labels with diagonal leader lines from anchor points
  drawGFAnnotations(ctx, profileData, schoolModeData) {
    const settings = window.store?.state?.settings || {}
    const cs = this.chartScale
    const fontSize = 9 * cs / this.scale
    const annotations = []

    // Main ceiling curve annotations
    if (!this.hiddenLayers.has('ceiling')) {
      const stopPoints = profileData.filter(p => p.segment?.type === 'stop')
      if (stopPoints.length > 0) {
        const gfLow = Math.round((settings.gfLow || 0.3) * 100)
        const gfHigh = Math.round((settings.gfHigh || 0.85) * 100)
        const color = 'rgba(248, 114, 114, 0.9)'
        const first = stopPoints[0]
        annotations.push({ anchorX: this.toX(first.time), anchorY: this.toY(first.depth), label: `GF Low ${gfLow}`, color, group: 'low' })
        const last = stopPoints[stopPoints.length - 1]
        if (last !== first) {
          annotations.push({ anchorX: this.toX(last.time), anchorY: this.toY(last.depth), label: `GF High ${gfHigh}`, color, group: 'high' })
        }
      }
    }

    // School mode curve annotations
    if (schoolModeData && schoolModeData.length > 0) {
      schoolModeData.forEach((comp, idx) => {
        if (this.hiddenLayers.has(`school-${idx}`)) return
        const color = this.schoolModeColors[idx % this.schoolModeColors.length].replace('0.7', '0.9')
        const compPoints = this.buildProfilePoints(comp)
        if (!compPoints) return
        const stopPoints = compPoints.filter(p => p.segment?.type === 'stop')
        if (stopPoints.length > 0) {
          const first = stopPoints[0]
          annotations.push({ anchorX: this.toX(first.time), anchorY: this.toY(first.depth), label: `GF Low ${comp.gfLow}`, color, group: 'low' })
          const last = stopPoints[stopPoints.length - 1]
          if (last !== first) {
            annotations.push({ anchorX: this.toX(last.time), anchorY: this.toY(last.depth), label: `GF High ${comp.gfHigh}`, color, group: 'high' })
          }
        }
      })
    }

    if (annotations.length === 0) return

    // Layout: separate low/high groups, align to common X, spread overlapping labels
    ctx.font = `bold ${fontSize}px sans-serif`
    ctx.textAlign = 'left'
    const lineHeight = fontSize * 1.6
    const armLength = 10 * cs / this.scale
    const gap = 3 * cs / this.scale

    for (const groupName of ['low', 'high']) {
      const group = annotations.filter(a => a.group === groupName)
      if (group.length === 0) continue
      const columnX = Math.max(...group.map(a => a.anchorX)) + armLength
      group.forEach(a => { a.columnX = columnX })
      group.sort((a, b) => a.anchorY - b.anchorY)
      group.forEach(a => { a.labelY = a.anchorY })
      for (let i = 1; i < group.length; i++) {
        if (group[i].labelY - group[i - 1].labelY < lineHeight) {
          group[i].labelY = group[i - 1].labelY + lineHeight
        }
      }
    }

    // Draw diagonal dashed leader lines, anchor dots, and label text
    const diagX = 15 * cs / this.scale
    const diagY = 10 * cs / this.scale
    for (const a of annotations) {
      const labelX = a.anchorX + diagX
      const labelYPos = a.labelY + diagY

      ctx.strokeStyle = a.color
      ctx.lineWidth = 1 * cs / this.scale
      ctx.setLineDash([3 * cs / this.scale, 3 * cs / this.scale])
      ctx.beginPath()
      ctx.moveTo(a.anchorX, a.anchorY)
      ctx.lineTo(labelX, labelYPos)
      ctx.stroke()
      ctx.setLineDash([])

      ctx.fillStyle = a.color
      ctx.beginPath()
      ctx.arc(a.anchorX, a.anchorY, 2 * cs / this.scale, 0, Math.PI * 2)
      ctx.fill()

      ctx.fillText(a.label, labelX + gap, labelYPos + fontSize * 0.35)
    }
  }

  // Draw gas change annotations on the profile chart.
  // Shows a colored label with gas name at each switch point,
  // connected by a diagonal dashed leader line (same style as GF annotations).
  drawGasChangeAnnotations(ctx, profileData) {
    if (this.hiddenLayers.has('ceiling')) return

    const gasChangePoints = profileData.filter(p => p.segment?.type === 'gasChange')
    if (gasChangePoints.length === 0) return

    const cs = this.chartScale
    const fontSize = 9 * cs / this.scale
    const color = 'rgba(56, 189, 248, 0.9)' // sky-400
    const armLength = 12 * cs / this.scale
    const gap = 3 * cs / this.scale

    ctx.font = `bold ${fontSize}px sans-serif`
    ctx.textAlign = 'left'

    for (const point of gasChangePoints) {
      const seg = point.segment
      const anchorX = this.toX(point.time)
      const anchorY = this.toY(point.depth)

      // Label: gas name (e.g. "Nx50", "O2")
      const label = seg.gas || seg.gasName || '?'

      // Draw downward-right diagonal leader line (same style as GF annotations)
      const labelX = anchorX + armLength * 4
      const labelY = anchorY + armLength * 4

      // Dashed leader line
      ctx.strokeStyle = color
      ctx.lineWidth = 1 * cs / this.scale
      ctx.setLineDash([3 * cs / this.scale, 3 * cs / this.scale])
      ctx.beginPath()
      ctx.moveTo(anchorX, anchorY)
      ctx.lineTo(labelX, labelY)
      ctx.stroke()
      ctx.setLineDash([])

      // Anchor dot
      ctx.fillStyle = color
      ctx.beginPath()
      ctx.arc(anchorX, anchorY, 2.5 * cs / this.scale, 0, Math.PI * 2)
      ctx.fill()

      // Small tank icon (rectangle + rounded top)
      const iconW = 5 * cs / this.scale
      const iconH = 7 * cs / this.scale
      const iconX = labelX - iconW - gap
      const iconY = labelY - iconH / 2
      ctx.fillStyle = color
      ctx.beginPath()
      ctx.roundRect(iconX, iconY, iconW, iconH, 1.5 * cs / this.scale)
      ctx.fill()
      // Tank valve (small rect on top)
      const valveW = 3 * cs / this.scale
      const valveH = 2 * cs / this.scale
      ctx.fillRect(iconX + (iconW - valveW) / 2, iconY - valveH + 0.5, valveW, valveH)

      // Gas name text
      ctx.fillStyle = color
      ctx.fillText(label, labelX + gap, labelY + fontSize * 0.35)
    }
  }

  // Draw depth and time axis labels (outside zoom transform)
  drawAxisLabels(ctx) {
    const cs = this.chartScale
    ctx.fillStyle = 'rgba(255,255,255,0.5)'
    ctx.font = `${10 * cs}px sans-serif`

    ctx.textAlign = 'right'
    for (let d = 0; d <= this.maxDepth; d += this.depthStep) {
      const y = this.toY(d)
      ctx.fillText(`${d}m`, this.padding - 5, y + 3)
    }

    ctx.textAlign = 'center'
    for (let t = 0; t <= this.maxTime; t += this.timeStep) {
      const x = this.toX(t)
      ctx.fillText(`${t}'`, x, this.canvasHeight - 15)
    }
  }

  // Main profile drawing orchestrator. Sets up canvas, computes scale
  // parameters, then delegates to sub-methods for each drawing layer.
  drawProfile() {
    const canvas = document.getElementById('profile-canvas')
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    const rect = canvas.getBoundingClientRect()
    const width = rect.width
    const height = rect.height
    canvas.width = width * 2
    canvas.height = height * 2
    ctx.scale(2, 2)

    const profileData = this.getProfileData()
    this.chartScale = window.store?.state?.settings?.chartScale || 1

    // Clear canvas
    ctx.fillStyle = getComputedStyle(document.body).getPropertyValue('--b2') || '#1d232a'
    ctx.fillRect(0, 0, width, height)

    const resetBtn = document.getElementById('reset-zoom-btn')
    if (resetBtn) {
      resetBtn.style.opacity = this.scale > 1 ? '1' : '0'
    }

    if (!profileData || profileData.length < 2) {
      ctx.fillStyle = 'rgba(255,255,255,0.5)'
      ctx.font = `${14 * this.chartScale}px sans-serif`
      ctx.textAlign = 'center'
      ctx.fillText(this.t('profile_calculate_hint'), width / 2, height / 2)
      return
    }

    // Compute bounds including school mode comparison plans
    const results = window.store.state.results
    let rawMaxDepth = Math.max(...profileData.map(p => Math.max(p.depth, p.ceiling)))
    let rawMaxTime = Math.max(...profileData.map(p => p.time))
    const schoolModeData = results?.schoolMode
    if (schoolModeData && schoolModeData.length > 0) {
      for (const comp of schoolModeData) {
        const compPoints = this.buildProfilePoints(comp)
        if (compPoints) {
          rawMaxDepth = Math.max(rawMaxDepth, ...compPoints.map(p => Math.max(p.depth, p.ceiling)))
          rawMaxTime = Math.max(rawMaxTime, ...compPoints.map(p => p.time))
        }
      }
    }

    // Find first deco stop depth for non-linear scale
    let plan
    if (this.profileMode === 'ccr' && results?.ccr) plan = results.ccr
    else if (this.profileMode === 'bailout' && results?.bailout) plan = results.bailout
    else plan = results?.ccr || results?.bailout
    const firstStopDepth = plan?.stops?.[0]?.depth || 0

    // Store scale parameters used by toY/toX and sub-methods
    this.canvasWidth = width
    this.canvasHeight = height
    this.padding = 35 * Math.max(1, this.chartScale * 0.75)
    this.maxDepth = Math.ceil(rawMaxDepth / 10) * 10 + 5
    this.maxTime = rawMaxTime + 5
    this.firstStopDepth = firstStopDepth
    this.useNonLinearScale = firstStopDepth > 0 && (rawMaxDepth - firstStopDepth) > 20
    this.decoZoneRatio = 0.85
    this.depthStep = this.maxDepth > 60 ? 20 : (this.maxDepth > 30 ? 10 : 5)
    this.timeStep = this.maxTime > 120 ? 30 : (this.maxTime > 60 ? 15 : 10)

    // Draw all layers inside zoom transform
    ctx.save()
    ctx.translate(this.offsetX, this.offsetY)
    ctx.scale(this.scale, this.scale)

    this.drawGrid(ctx)
    this.drawMainProfile(ctx, profileData)
    this.drawSchoolModeProfiles(ctx, schoolModeData)
    this.drawInteractivePoints(ctx, profileData, schoolModeData)
    this.drawGFAnnotations(ctx, profileData, schoolModeData)
    this.drawGasChangeAnnotations(ctx, profileData)

    ctx.restore()

    // Axis labels are drawn outside zoom transform
    this.drawAxisLabels(ctx)
    this.updateSchoolMode()
  }

  updateSchoolMode() {
    const legendEl = document.getElementById('school-mode-legend')
    const tableCard = document.getElementById('school-mode-table-card')
    const tableEl = document.getElementById('school-mode-table')
    if (!legendEl || !tableCard || !tableEl) return

    const results = window.store.state.results
    const schoolData = results?.schoolMode
    const settings = window.store.state.settings
    const currentGfLow = Math.round((settings.gfLow || 0.3) * 100)
    const currentGfHigh = Math.round((settings.gfHigh || 0.85) * 100)

    // Always show main GF in legend
    const colors = [
      'rgba(251, 146, 60, 0.7)',
      'rgba(34, 211, 238, 0.7)',
      'rgba(250, 204, 21, 0.7)'
    ]
    const isSchoolActive = schoolData && schoolData.length > 0
    const mainLegendColor = isSchoolActive ? 'rgba(248, 114, 114, 0.8)' : 'rgba(74, 222, 128, 0.8)'
    legendEl.innerHTML = `
      <span class="flex items-center gap-1 cursor-pointer school-legend-toggle ${this.hiddenLayers.has('ceiling') ? 'opacity-30 line-through' : ''}" data-layer="ceiling">
        <span class="w-3 h-0.5 inline-block" style="background: ${mainLegendColor}"></span>
        GF ${currentGfLow}/${currentGfHigh}
      </span>` + (isSchoolActive ? schoolData.map((comp, idx) => `
      <span class="flex items-center gap-1 cursor-pointer school-legend-toggle ${this.hiddenLayers.has('school-' + idx) ? 'opacity-30 line-through' : ''}" data-layer="school-${idx}">
        <span class="w-3 h-0.5 inline-block" style="background: ${colors[idx % colors.length]}"></span>
        GF ${comp.gfLow}/${comp.gfHigh}
      </span>
    `).join('') : '')

    // Bind click handlers on legend items
    legendEl.querySelectorAll('.school-legend-toggle').forEach(item => {
      item.addEventListener('click', () => {
        const layer = item.dataset.layer
        if (this.hiddenLayers.has(layer)) {
          this.hiddenLayers.delete(layer)
        } else {
          this.hiddenLayers.add(layer)
        }
        this.drawProfile()
      })
    })

    if (!schoolData || schoolData.length === 0) {
      tableCard.classList.add('hidden')
      return
    }

    // Update comparison table
    const mainPlan = results.ccr || results.bailout
    const stopsLabel = this.t('profile_school_stops')
    const ttsLabel = this.t('profile_school_tts')
    const ceilingColor = 'rgba(248, 114, 114, 0.8)'

    let rows = `
      <tr class="font-bold">
        <td><span class="inline-block w-2 h-2 rounded-full mr-1" style="background: ${ceilingColor}"></span>GF ${currentGfLow}/${currentGfHigh}</td>
        <td>${Math.ceil(mainPlan?.tts || 0)} min</td>
        <td>${mainPlan?.stops?.length || 0}</td>
      </tr>`
    schoolData.forEach((comp, idx) => {
      const c = colors[idx % colors.length]
      rows += `
        <tr>
          <td><span class="inline-block w-2 h-2 rounded-full mr-1" style="background: ${c}"></span><span style="color: ${c}">GF ${comp.gfLow}/${comp.gfHigh}</span></td>
          <td style="color: ${c}">${Math.ceil(comp.tts || 0)} min</td>
          <td style="color: ${c}">${comp.stops?.length || 0}</td>
        </tr>`
    })

    tableEl.innerHTML = `
      <table class="table table-xs">
        <thead><tr><th>Preset</th><th>${ttsLabel}</th><th>${stopsLabel}</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>`
    tableCard.classList.remove('hidden')
  }

  resetZoom() {
    this.scale = 1
    this.offsetX = 0
    this.offsetY = 0
    this.drawProfile()
  }

  constrainPan() {
    const canvas = document.getElementById('profile-canvas')
    if (!canvas) return

    const rect = canvas.getBoundingClientRect()
    const width = rect.width
    const height = rect.height

    // Calculate bounds
    const scaledWidth = width * this.scale
    const scaledHeight = height * this.scale

    // Limit panning so content doesn't go off screen
    const maxOffsetX = 0
    const minOffsetX = width - scaledWidth
    const maxOffsetY = 0
    const minOffsetY = height - scaledHeight

    this.offsetX = Math.min(maxOffsetX, Math.max(minOffsetX, this.offsetX))
    this.offsetY = Math.min(maxOffsetY, Math.max(minOffsetY, this.offsetY))
  }

  // Bind all canvas interaction events and UI controls.
  // Delegates to sub-methods for each interaction group.
  bindEvents() {
    const canvas = document.getElementById('profile-canvas')
    if (!canvas) return

    const handleInteraction = (clientX, clientY) => {
      const rect = canvas.getBoundingClientRect()
      const x = clientX - rect.left
      const y = clientY - rect.top

      for (const point of this.interactivePoints) {
        const dx = x - point.x
        const dy = y - point.y
        if (Math.sqrt(dx * dx + dy * dy) <= point.radius + 5) {
          this.selectedSegmentIndex = point.index
          this.updateTissueBars(point.segment)
          this.drawProfile()
          return true
        }
      }
      return false
    }

    this.bindTouchEvents(canvas, handleInteraction)
    this.bindMouseEvents(canvas, handleInteraction)
    this.bindUIControls()
  }

  // Touch events: tap to select point, double-tap to reset zoom, pinch-zoom, pan
  bindTouchEvents(canvas, handleInteraction) {
    const getPinchDistance = (touches) => {
      const dx = touches[0].clientX - touches[1].clientX
      const dy = touches[0].clientY - touches[1].clientY
      return Math.sqrt(dx * dx + dy * dy)
    }

    const getPinchCenter = (touches, rect) => ({
      x: (touches[0].clientX + touches[1].clientX) / 2 - rect.left,
      y: (touches[0].clientY + touches[1].clientY) / 2 - rect.top
    })

    canvas.addEventListener('touchstart', (e) => {
      e.preventDefault()
      if (e.touches.length === 2) {
        this.lastPinchDistance = getPinchDistance(e.touches)
        this.isPanning = false
      } else if (e.touches.length === 1) {
        const now = Date.now()
        if (now - this.lastTapTime < 300 && this.scale > 1) {
          this.resetZoom()
          this.lastTapTime = 0
          return
        }
        this.lastTapTime = now
        const touch = e.touches[0]
        if (!handleInteraction(touch.clientX, touch.clientY) && this.scale > 1) {
          this.isPanning = true
          this.lastTouchX = touch.clientX
          this.lastTouchY = touch.clientY
        }
      }
    })

    canvas.addEventListener('touchmove', (e) => {
      e.preventDefault()
      if (e.touches.length === 2) {
        const rect = canvas.getBoundingClientRect()
        const newDistance = getPinchDistance(e.touches)
        const center = getPinchCenter(e.touches, rect)
        if (this.lastPinchDistance) {
          const newScale = Math.min(this.maxScale, Math.max(this.minScale, this.scale * (newDistance / this.lastPinchDistance)))
          if (newScale !== this.scale) {
            const scaleChange = newScale / this.scale
            this.offsetX = center.x - (center.x - this.offsetX) * scaleChange
            this.offsetY = center.y - (center.y - this.offsetY) * scaleChange
            this.scale = newScale
            this.constrainPan()
            this.drawProfile()
          }
        }
        this.lastPinchDistance = newDistance
      } else if (e.touches.length === 1 && this.isPanning) {
        const touch = e.touches[0]
        this.offsetX += touch.clientX - this.lastTouchX
        this.offsetY += touch.clientY - this.lastTouchY
        this.constrainPan()
        this.drawProfile()
        this.lastTouchX = touch.clientX
        this.lastTouchY = touch.clientY
      }
    })

    canvas.addEventListener('touchend', (e) => {
      if (e.touches.length < 2) this.lastPinchDistance = null
      if (e.touches.length === 0) this.isPanning = false
    })
  }

  // Mouse events: click to select point, drag-pan when zoomed, wheel-zoom
  bindMouseEvents(canvas, handleInteraction) {
    let mouseDown = false
    let mouseMoved = false
    let mouseStartX = 0
    let mouseStartY = 0

    canvas.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return
      mouseDown = true
      mouseMoved = false
      mouseStartX = e.clientX
      mouseStartY = e.clientY
      if (this.scale > 1) canvas.style.cursor = 'grabbing'
    })

    canvas.addEventListener('mousemove', (e) => {
      if (!mouseDown) return
      const dx = e.clientX - mouseStartX
      const dy = e.clientY - mouseStartY
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) mouseMoved = true
      if (mouseMoved && this.scale > 1) {
        this.offsetX += dx
        this.offsetY += dy
        this.constrainPan()
        this.drawProfile()
        mouseStartX = e.clientX
        mouseStartY = e.clientY
      }
    })

    canvas.addEventListener('mouseup', (e) => {
      if (mouseDown && !mouseMoved) handleInteraction(e.clientX, e.clientY)
      mouseDown = false
      mouseMoved = false
      canvas.style.cursor = ''
    })

    canvas.addEventListener('mouseleave', () => {
      mouseDown = false
      mouseMoved = false
      canvas.style.cursor = ''
    })

    canvas.addEventListener('wheel', (e) => {
      e.preventDefault()
      const rect = canvas.getBoundingClientRect()
      const mouseX = e.clientX - rect.left
      const mouseY = e.clientY - rect.top
      const newScale = Math.min(this.maxScale, Math.max(this.minScale, this.scale * (e.deltaY > 0 ? 0.9 : 1.1)))
      if (newScale !== this.scale) {
        const scaleChange = newScale / this.scale
        this.offsetX = mouseX - (mouseX - this.offsetX) * scaleChange
        this.offsetY = mouseY - (mouseY - this.offsetY) * scaleChange
        this.scale = newScale
        this.constrainPan()
        this.drawProfile()
      }
    }, { passive: false })
  }

  // UI controls: reset zoom, chart scale +/-, tissue bar toggle, fullscreen, profile tabs
  bindUIControls() {
    document.getElementById('reset-zoom-btn')?.addEventListener('click', () => {
      this.resetZoom()
    })

    const scaleSteps = [1, 1.5, 2, 3]
    document.getElementById('chart-scale-up')?.addEventListener('click', () => {
      const current = window.store?.state?.settings?.chartScale || 1
      const idx = scaleSteps.indexOf(current)
      const next = idx < scaleSteps.length - 1 ? scaleSteps[idx + 1] : current
      if (next !== current) {
        window.store.update('settings.chartScale', next)
        const label = document.getElementById('chart-scale-label')
        if (label) label.textContent = `${next}x`
      }
    })
    document.getElementById('chart-scale-down')?.addEventListener('click', () => {
      const current = window.store?.state?.settings?.chartScale || 1
      const idx = scaleSteps.indexOf(current)
      const prev = idx > 0 ? scaleSteps[idx - 1] : current
      if (prev !== current) {
        window.store.update('settings.chartScale', prev)
        const label = document.getElementById('chart-scale-label')
        if (label) label.textContent = `${prev}x`
      }
    })

    document.getElementById('tissue-bars')?.addEventListener('click', (e) => {
      const toggle = e.target.closest('.tissue-bar-toggle')
      if (!toggle) return
      const idx = parseInt(toggle.dataset.compartment, 10)
      if (isNaN(idx)) return
      this.expandedCompartment = this.expandedCompartment === idx ? null : idx
      const tissueBars = document.getElementById('tissue-bars')
      if (tissueBars && this.currentSegmentCompartments) {
        tissueBars.innerHTML = this.renderTissueBars(
          this.currentSegmentCompartments,
          this.currentSegmentTissueInfo,
          this.currentSegmentCeiling,
          this.currentSegmentDepth
        )
      }
    })

    document.getElementById('fullscreen-profile-btn')?.addEventListener('click', () => {
      toggleFullscreen('profile-fullscreen-container', () => {
        setTimeout(() => this.drawProfile(), 100)
      })
    })

    this.container.querySelectorAll('.profile-tab').forEach(tab => {
      tab.addEventListener('click', (e) => {
        const mode = e.target.dataset.mode
        if (mode !== this.profileMode) {
          this.profileMode = mode
          this.resetZoom()
          this.selectedSegmentIndex = null
          this.container.querySelectorAll('.profile-tab').forEach(t => {
            t.classList.toggle('tab-active', t.dataset.mode === mode)
          })
          const tissueBars = document.getElementById('tissue-bars')
          const segmentInfo = document.getElementById('segment-info')
          if (tissueBars) tissueBars.innerHTML = this.renderTissueBars(null)
          if (segmentInfo) segmentInfo.textContent = ''
        }
      })
    })
  }



  updateTissueBars(segment) {
    const tissueBars = document.getElementById('tissue-bars')
    const segmentInfo = document.getElementById('segment-info')

    if (!tissueBars || !segment) return

    if (segment.compartments) {
      const depth = segment.depth || segment.endDepth

      // Store segment data for re-renders on compartment toggle
      this.currentSegmentDepth = depth
      this.currentSegmentCompartments = segment.compartments
      this.currentSegmentTissueInfo = segment.tissueInfo
      this.currentSegmentCeiling = segment.ceiling

      // Default: expand leading compartment
      if (segment.tissueInfo) {
        this.expandedCompartment = segment.tissueInfo.compartment - 1
      }

      tissueBars.innerHTML = this.renderTissueBars(segment.compartments, segment.tissueInfo, segment.ceiling, depth)

      // Update segment info
      let typeLabel
      if (segment.type === 'surface') {
        typeLabel = this.t('profile_surface') || 'Surface'
      } else if (segment.type === 'stop') {
        typeLabel = this.t('plan_stop')
      } else if (segment.type === 'bottom') {
        typeLabel = this.t('plan_bottom')
      } else {
        typeLabel = segment.type
      }
      const ceilingText = segment.ceiling > 0 ? ` | ${this.t('profile_ceiling')}: ${Math.round(segment.ceiling)}m` : ''

      if (segmentInfo) {
        if (segment.type === 'surface') {
          segmentInfo.textContent = `${typeLabel} | ${this.t('plan_runtime')}: ${Math.ceil(segment.runtime)}min | ${this.t('profile_residual') || 'Residual N2'}`
        } else {
          segmentInfo.textContent = `${typeLabel} @ ${depth}m | ${this.t('plan_runtime')}: ${Math.ceil(segment.runtime)}min${ceilingText}`
        }
      }
    }
  }
}
